import { BitkitAlert, BitkitButton, BitkitCheckbox, BitkitLink, BitkitList, BitkitTextInput } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import { useState } from 'react';

import URLs from '@/enums/urls';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { useLastUsedEmail } from '@/hooks/useLastUsedEmail';
import TermsOfServiceAndPrivacyPolicy from '@/pages/SignUp/components/TermsOfServiceAndPrivacyPolicy/TermsOfServiceAndPrivacyPolicy';

const SSOEmailForm = ({
  requireConsent,
  rememberMe,
  onRememberMeChanged,
}: {
  requireConsent?: boolean;
  rememberMe?: boolean;
  onRememberMeChanged?: (value: boolean) => void;
}) => {
  const [state, setState] = useState<'email' | 'sent' | 'resent'>('email');
  const [lastSSOEmail, setLastSSOEmail] = useLastUsedEmail();
  const [email, setEmail] = useState(lastSSOEmail || '');
  const [consent, setConsent] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const { mutate, reset, isPending } = useMonolithApiMutation('/users/sign-in/saml', 'post', {
    onSuccess(data) {
      const url = data.headers.location;
      if (typeof url === 'string') {
        window.location.assign(url);
      } else {
        setState(state === 'sent' ? 'resent' : 'sent');
      }
    },
    onSettled() {
      reset();
    },
  });
  const needsConsent = requireConsent && !consent;
  const valid = email && !emailError && !needsConsent;

  const onSubmit = () => {
    mutate({ email, rememberMe });
    if (rememberMe) {
      setLastSSOEmail(email);
    }
  };

  if (state === 'email') {
    return (
      <Box display="flex" flexDirection="column" gap="16">
        <BitkitTextInput
          label="Email"
          value={email}
          errorText={emailError ? 'Not valid email' : undefined}
          placeholder="Enter your work email"
          onChange={(ev) => {
            setEmailError(Boolean(ev.target.value) && !ev.target.validity.valid);
            setEmail(ev.target.value);
          }}
          inputProps={{
            onKeyUp: (ev) => {
              if (ev.key === 'Enter' && valid) {
                onSubmit();
              }
            },
            type: 'email',
          }}
        />
        {requireConsent ? (
          <TermsOfServiceAndPrivacyPolicy
            checked={consent}
            onCheckedChange={(details) => setConsent(details.checked === true)}
          />
        ) : (
          <BitkitCheckbox
            checked={!!rememberMe}
            labelText="Keep me logged in"
            onCheckedChange={(details) => onRememberMeChanged?.(details.checked === true)}
          />
        )}
        <BitkitButton state={isPending ? 'loading' : !valid ? 'disabled' : undefined} onClick={onSubmit}>
          Continue with SSO
        </BitkitButton>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="16">
      <Box display="flex" flexDirection="column" gap="4">
        <Text textStyle="heading/h3">Check your inbox!</Text>
        <Text textStyle="body/md/regular">
          We&lsquo;ve sent a secure sign-in link to your email address,{' '}
          <Text as="span" textStyle="body/md/semibold">
            {email}
          </Text>
          . Check the spam folder too.
        </Text>
      </Box>
      <Separator />
      <Box display="flex" flexDirection="column" gap="8">
        <Text textStyle="heading/h5">Having trouble signing in?</Text>
        <BitkitList>
          <BitkitList.Item>
            <Text textStyle="body/md/regular">
              Didn&lsquo;t receive the email? Request another sign-in link or{' '}
              <BitkitLink colorVariant="purple" href={URLs.CONTACT_SUPPORT} isExternal>
                reach out to support
              </BitkitLink>
              .
            </Text>
          </BitkitList.Item>
          <BitkitList.Item>
            <Text textStyle="body/md/regular">
              Try using your single sign-on provider by visiting your company&lsquo;s login portal to authenticate your
              account.
            </Text>
          </BitkitList.Item>
        </BitkitList>
      </Box>
      {state === 'resent' ? (
        <BitkitAlert variant="success" messageText="Please check your inbox." />
      ) : (
        <BitkitButton size="md" variant="secondary" onClick={onSubmit}>
          Resend email
        </BitkitButton>
      )}
    </Box>
  );
};

export default SSOEmailForm;
